import { baseApi } from '../apiProvider';
import {
  RequestLiveList,
  IRequestLiveList,
} from '../models/request/requestLiveList';
import { RequestStartLive } from '../models/request/requestStartLive';
import { ResponseAudience } from '../models/response/responseAudience';
import {
  ResponseJoinLive,
  ResponseLiveCoin,
  ResponseLiveInfo,
  IResponseLive,
} from '../models/response/responseLive';
import { ResponseLiveResult } from '../models/response/responseLiveResult';
import { ResponseLiveSetting } from '../models/response/responseLiveSetting';

const liveAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // get live list
    getListLive: builder.mutation<IResponseLive[], IRequestLiveList>({
      query: (params: IRequestLiveList) => ({
        url: `/v0/sessional-live-streams`,
        method: 'GET',
        params,
      }),
    }),
    // get live by league level
    getLiveByLeague: builder.mutation<ResponseLiveInfo[], number>({
      query: (league: number) => ({
        url: `/v0/live/leagues/${league}`,
        method: 'GET',
      }),
    }),
    // get live of rookies
    getLiveRookies: builder.mutation<ResponseLiveInfo[], void>({
      query: () => ({
        url: '/v0/live/rookies',
        method: 'GET',
      }),
    }),
    // start live
    requestStartLive: builder.mutation<ResponseLiveInfo, RequestStartLive>({
      query: (data: RequestStartLive) => ({
        url: `/v0/live`,
        method: 'POST',
        body: data,
      }),
    }),
    getFollowingLiveList: builder.query<
      Array<ResponseLiveInfo>,
      RequestLiveList
    >({
      query: (params: RequestLiveList) => ({
        url: `/v0/live/follow`,
        method: 'GET',
        params,
      }),
    }),
    getLiveInfo: builder.mutation<ResponseLiveInfo, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}`,
        method: 'GET',
      }),
    }),
    joinLive: builder.mutation<ResponseJoinLive, string>({
      query: (liveId: string) => ({
        url: `/v0/joins/live/${liveId}`,
        method: 'POST',
      }),
    }),
    endViewLive: builder.mutation<void, string>({
      query: (liveId: string) => ({
        url: `/v0/joins/live/${liveId}`,
        method: 'DELETE',
      }),
    }),
    endDeliveryLive: builder.mutation<void, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}`,
        method: 'DELETE',
      }),
    }),
    getLiveAudiences: builder.mutation<ResponseAudience[], string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/audiences`,
        method: 'GET',
      }),
    }),
    getLiveCoin: builder.mutation<ResponseLiveCoin, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/berry-coins`,
        method: 'GET',
      }),
    }),
    getLiveResult: builder.mutation<ResponseLiveResult, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/result`,
        method: 'GET',
      }),
    }),
    getLiveSetting: builder.mutation<ResponseLiveSetting, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/setting`,
        method: 'GET',
      }),
    }),
    putMirrorOn: builder.mutation<ResponseLiveSetting, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/setting/mirror/on`,
        method: 'PUT',
      }),
    }),
    putMirrorOff: builder.mutation<ResponseLiveSetting, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/setting/mirror/off`,
        method: 'PUT',
      }),
    }),
    getGiftTopAudience: builder.mutation<Array<ResponseAudience>, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/audiences/gift-top`,
        method: 'GET',
      }),
    }),
    putMuteOn: builder.mutation<ResponseLiveSetting, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/setting/mute/on`,
        method: 'PUT',
      }),
    }),
    putMuteOff: builder.mutation<ResponseLiveSetting, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/setting/mute/off`,
        method: 'PUT',
      }),
    }),
    sendLike: builder.mutation<void, string>({
      query: (liveId: string) => ({
        url: `/v0/live/${liveId}/nice`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetListLiveMutation,
  useGetLiveByLeagueMutation,
  useGetLiveRookiesMutation,
  useRequestStartLiveMutation,
  useGetLiveInfoMutation,
  useJoinLiveMutation,
  useEndDeliveryLiveMutation,
  useEndViewLiveMutation,
  useGetLiveAudiencesMutation,
  useGetLiveResultMutation,
  useGetLiveCoinMutation,
  useGetLiveSettingMutation,
  usePutMirrorOnMutation,
  usePutMirrorOffMutation,
  useGetGiftTopAudienceMutation,
  usePutMuteOffMutation,
  usePutMuteOnMutation,
  useSendLikeMutation,
  useGetFollowingLiveListQuery,
} = liveAPI;
